import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import useUserStore from "../../stores/useUserStore";
import dayjs from "dayjs";
import { useApi } from "../../apiHelpers";
import DroneLogsSection from "../DroneLogsSection";
import DroneRepair from "../../Parts/Pulls/DroneRepair";
import { buttonClass } from "../../components/ui/Button";
import { Tabs } from "../../components/ui/Tabs";
import { Field } from "../../components/ui/Field";
import { Checkbox } from "../../components/ui/Checkbox";
import { Panel } from "../../components/ui/Panel";
import { EmptyState } from "../../components/ui/EmptyState";
import FadeIn from "../../components/ui/FadeIn";
import { CATEGORY, alpha } from "../../components/ui/categoryColors";
import { Skeleton, SkeletonGroup } from "../../components/ui/Skeleton";

/**
 * DroneHistory - Combined view of maintenance, flags, and RMAs
 *
 * Props:
 * - drone_serial: string - Drone serial number
 * - simpleMode: boolean - If true, hides add entry form
 * - onRMAClick: function - Callback when RMA is clicked (rma_number)
 * - hideTabs: boolean - If true, hides tabs and shows only history (compact mode)
 */
const HISTORY_TABS = [
    { key: 'history', label: 'History' },
    { key: 'logs', label: 'Flight Logs' },
];

const DroneHistory = ({ drone_serial, simpleMode, onRMAClick, hideTabs }) => {
    const user = useUserStore((state) => state.user);
    const canRepairPull = useUserStore((state) => state.hasCapability('operations', 'manufacturing'));
    const api = useApi();

    const [droneKey] = useState(drone_serial);
    const [historyItems, setHistoryItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [maintenance, setMaintenance] = useState([]);
    const [newLogCheck, setNewLogCheck] = useState(false);
    const [activeTab, setActiveTab] = useState('history');

    useEffect(() => {
        fetchAllHistory();
    }, [droneKey]);

    const fetchAllHistory = async () => {
        setLoading(true);

        try {
            // Fetch all four data sources in parallel
            const [maintLogs, flags, rmas, reports, installs] = await Promise.all([
                api.get(`/maintenance/${droneKey}`, { silent: true }).catch(() => []),
                api.get(`/flags/all/${droneKey}`, { silent: true }).catch(() => []),
                api.get(`/rma/drone/${droneKey}`, { silent: true }).catch(() => []),
                api.get(`/reports/${droneKey}`, { silent: true }).catch(() => []),
                api.get(`/scanner/installs/${droneKey}`, { silent: true }).catch(() => ({ events: [] }))
            ]);

            // Process and combine all items
            const maintItems = arrangeMaint(maintLogs || []);
            const flagItems = arrangeFlags(flags || []);
            const rmaItems = arrangeRmas(rmas || []);
            const reportItems = arrangeReports(reports || []);
            const installItems = arrangeInstalls(installs?.events || []);

            // Combine and sort
            const combined = [...maintItems, ...flagItems, ...rmaItems, ...reportItems, ...installItems];

            // Sort: Active RMAs first, then by date descending
            combined.sort((a, b) => {
                // Active RMAs float to top
                if (a.isActiveRma && !b.isActiveRma) return -1;
                if (!a.isActiveRma && b.isActiveRma) return 1;
                // Then sort by date
                return b.sortDate - a.sortDate;
            });

            setHistoryItems(combined);
        } catch (err) {
            console.error('Error fetching history:', err);
        } finally {
            setLoading(false);
        }
    };

    const arrangeMaint = (logs) => {
        return logs.map((log) => ({
            id: `maint-${log.id}`,
            date: dayjs(log.dateofservice).format("MM-DD-YY"),
            sortDate: dayjs(log.dateofservice).valueOf(),
            desc: log.descript,
            user: log.firstname || 'Unknown',
            type: 'maint',
            isActiveRma: false
        }));
    };

    // Component genealogy (part_install_events). Assembly used to write this as
    // prose into maintlist, so these entries USED to arrive in the maint lane;
    // they are their own lane now, and are structured rather than parsed.
    const arrangeInstalls = (events) => {
        return events.map((e) => ({
            id: `install-${e.id}`,
            date: dayjs(e.occurred_at).format("MM-DD-YY"),
            sortDate: dayjs(e.occurred_at).valueOf(),
            desc: `${e.action === 'install' ? 'Fitted' : 'Removed'} ${e.component_type}`
                + ` ${e.board_uid || e.part_sku || ''}`.trimEnd()
                + (e.source === 'backfill' ? ' (reconstructed, date approximate)' : ''),
            user: e.operator_firstname || 'Unknown',
            type: 'install',
            isActiveRma: false
        }));
    };

    const arrangeFlags = (flags) => {
        return flags.map((log) => ({
            id: `flag-${log.id}`,
            date: dayjs(log.dateflagged).format("MM-DD-YY"),
            sortDate: dayjs(log.dateflagged).valueOf(),
            desc: log.descript,
            user: log.firstname || 'Unknown',
            type: 'flag',
            status: log.active ? 'active' : 'resolved',
            isActiveRma: false
        }));
    };

    const arrangeReports = (reports) => {
        return reports.map((report) => ({
            id: `report-${report.id}`,
            date: dayjs(report.date_reported).format("MM-DD-YY"),
            sortDate: dayjs(report.date_reported).valueOf(),
            desc: report.issue_message,
            user: report.server_id || 'Unknown',
            type: 'report',
            isActiveRma: false
        }));
    };

    const arrangeRmas = (rmas) => {
        return rmas.map((rma) => {
            // Use item_status for this specific drone's item, with repair_status detail
            const itemStatus = rma.item_status || 'pending_review';
            const repairStatus = rma.repair_status;

            // Determine display status: show repair status if approved, otherwise item status
            let displayStatus = itemStatus;
            if (itemStatus === 'approved' && repairStatus && repairStatus !== 'pending') {
                displayStatus = repairStatus;
            }

            const isActive = itemStatus === 'pending_review' ||
                (itemStatus === 'approved' && !['completed', 'shipped'].includes(repairStatus));

            // Format issue category for display
            const category = rma.issue_category
                ? rma.issue_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                : (rma.item_name || 'RMA');
            return {
                id: `rma-${rma.id}`,
                date: dayjs(rma.created_at).format("MM-DD-YY"),
                sortDate: dayjs(rma.created_at).valueOf(),
                desc: rma.issue_description || category,
                category: category,
                user: rma.customer_name || rma.customer_email,
                type: 'rma',
                status: displayStatus,
                itemStatus: itemStatus,
                rma_number: rma.rma_number,
                isActiveRma: isActive
            };
        });
    };

    // Entry kinds are categories: one hue each, drawn from the shared OKLCH family.
    const itemHue = (item) => {
        if (item.type === 'maint') return 'blue';
        if (item.type === 'flag') return item.status === 'active' ? 'red' : 'orange';
        if (item.type === 'rma') return item.isActiveRma ? null : 'green';
        if (item.type === 'report') return 'purple';
        if (item.type === 'install') return 'teal';
        return null;
    };

    const getItemStyle = (item) => {
        if (item.type === 'rma') return item.isActiveRma
            ? 'border-l-primary bg-primary/10 hover:bg-primary/20 cursor-pointer border-2 border-primary/30 shadow-[0_0_8px_rgba(255,204,51,0.2)]'
            : 'cursor-pointer hover:brightness-125';
        return itemHue(item) ? '' : 'border-l-outline';
    };

    const getItemColour = (item) => {
        const hue = itemHue(item);
        if (!hue) return undefined;
        return { borderLeftColor: CATEGORY[hue].mark.oklch, backgroundColor: alpha(CATEGORY[hue].mark, 0.12) };
    };

    const labelInk = (hue) => ({ color: CATEGORY[hue].ink.oklch });

    const getTypeLabel = (item) => {
        if (item.type === 'maint') return <span className="text-xs" style={labelInk('blue')}>MAINT</span>;
        if (item.type === 'flag') return <span className="text-xs" style={labelInk('orange')}>FLAG</span>;
        if (item.type === 'rma') return <span className="text-primary text-xs font-bold">RMA</span>;
        if (item.type === 'report') return <span className="text-xs" style={labelInk('purple')}>RPRT</span>;
        if (item.type === 'install') return <span className="text-xs" style={labelInk('teal')}>BUILD</span>;
        return null;
    };

    // A hue name for the statuses that carry one; the rest read as secondary text.
    const STATUS_HUE = {
        pending_review: 'yellow',
        approved: 'blue',
        rejected: 'red',
        received: 'indigo',
        in_repair: 'orange',
        repaired: 'cyan',
        quality_check: 'cyan',
        completed: 'green',
        unrepairable: 'red',
        awaiting_receipt: 'purple',
        shipped: 'teal',
        on_hold: 'amber',
    };
    const getStatusStyle = (status) => (STATUS_HUE[status] ? labelInk(STATUS_HUE[status]) : undefined);

    // Handle new maintenance entry
    const handleSaveLogs = async () => {
        const logAsSeenNeeded = maintenance.some((log) => log.logAsSeen);

        for (const log of maintenance) {
            try {
                // user_id is automatically taken from JWT on the backend
                await api.post(`/maintenance`, {
                    body: {
                        drone_serial: droneKey,
                        descript: log.desc,
                    },
                    errorAlert: "Can't add logs, please try again"
                });
            } catch (error) {
                return;
            }
        }

        setMaintenance([]);
        setNewLogCheck(false);
        fetchAllHistory();

        if (logAsSeenNeeded) {
            handleSeen();
        }
    };

    const handleSeen = async () => {
        try {
            const displayName = user.lastname
                ? `${user.firstname} ${user.lastname.charAt(0)}.`
                : user.firstname;
            await api.post(`/location-history/${droneKey}/maintenance`, {
                body: { userName: displayName }
            });
        } catch (err) {
            console.error("Seen failed:", err);
        }
    };

    const addMaintenance = () => {
        setMaintenance((prev) => [...prev, { desc: "", logAsSeen: true }]);
        setNewLogCheck(true);
    };

    const handleMaintChange = (e) => {
        const index = parseInt(e.target.dataset.id);
        const value = e.target.value;
        setMaintenance((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], desc: value };
            return updated;
        });
    };

    const handleCheckboxChange = (e) => {
        const index = parseInt(e.target.dataset.id);
        const checked = e.target.checked;
        setMaintenance((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], logAsSeen: checked };
            return updated;
        });
    };

    return (
        <Panel label="Drone history" padding={hideTabs ? 'none' : 'md'} className={hideTabs ? '' : 'mb-3'}>
            {/* Tabs - hidden in compact mode */}
            {!hideTabs && (
                <div className="flex gap-1 mb-3 border-b border-gray-700">
                    <Tabs label="Drone history" tabs={HISTORY_TABS} value={activeTab} onChange={setActiveTab} />
                    <div className="flex-1" />
                    <Link
                        to={`/drone/history/${droneKey}`}
                        className="text-sm text-primary hover:underline self-center"
                    >
                        [expand]
                    </Link>
                </div>
            )}

            {/* Tab Content - in compact mode, always show history */}
            {(hideTabs || activeTab === 'history') ? (
                <FadeIn key="history">

                <FadeIn key={loading ? 'loading' : historyItems.length === 0 ? 'empty' : 'list'}>
                {loading ? (
                        <SkeletonGroup label="Loading history" className="space-y-1">
                        <Skeleton className="h-12 w-full" />
                        <Skeleton className="h-12 w-full" />
                        <Skeleton className="h-12 w-full" />
                    </SkeletonGroup>
                ) : historyItems.length === 0 ? (
                    <EmptyState variant="area">No history</EmptyState>
                ) : (
                    <ul className="space-y-1 max-h-48 overflow-y-auto">
                        {historyItems.map((item) => (
                            <li
                                key={item.id}
                                className={`p-2 rounded border-l-4 transition-[filter,background-color] duration-200 ${getItemStyle(item)}`}
                                style={getItemColour(item)}
                                onClick={() => item.type === 'rma' && onRMAClick && onRMAClick(item.rma_number)}
                            >
                                {item.type === 'rma' ? (
                                    /* RMA Special Layout */
                                    <>
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                                <div className="bg-primary/20 px-2 py-0 rounded border border-primary/50 shrink-0">
                                                    {getTypeLabel(item)}
                                                </div>
                                                <div className="text-white text-xs font-medium truncate">
                                                    {item.category}
                                                </div>
                                                <span className="text-on-surface-disabled">|</span>
                                                <span className="text-xs shrink-0 text-on-surface-variant"
                                                      style={getStatusStyle(item.status)}>
                                                    {item.itemStatus === 'rejected' ? 'Rejected' :
                                                     item.itemStatus === 'pending_review' ? 'Pending Review' :
                                                     `Repair: ${(item.status || 'pending').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`}
                                                </span>
                                            </div>
                                            <div className="flex items-center shrink-0">
                                                <span className="hidden md:inline text-primary text-lg">››</span>
                                            </div>
                                        </div>
                                        <div className="text-xs text-on-surface-variant">
                                            <span className="font-mono">{item.date}</span>
                                        </div>
                                    </>
                                ) : (
                                    /* Standard Layout for Maint/Flags */
                                    <>
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className="shrink-0">
                                                {getTypeLabel(item)}
                                            </div>
                                            <div className="flex-1 text-white text-sm truncate">
                                                {item.desc.startsWith("http") ? (
                                                    <a
                                                        href={item.desc}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-primary hover:underline"
                                                    >
                                                        LOG PULLED
                                                    </a>
                                                ) : (
                                                    item.desc
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-on-surface-variant">
                                            <span className="font-mono">{item.date}</span>
                                            <span>•</span>
                                            <span className="truncate">{item.user}</span>
                                        </div>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
                </FadeIn>

                {/* Add Entry Form */}
                {!simpleMode && (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                        <FadeIn key={newLogCheck ? 'form' : 'button'}>
                        {!newLogCheck ? (
                            <div className="flex justify-center gap-3">
                                <button
                                    type="button"
                                    onClick={addMaintenance}
                                    className={buttonClass('accent', 'md')}
                                >
                                    + Add Entry
                                </button>
                                {canRepairPull && <DroneRepair serialnumber={droneKey} />}
                            </div>
                        ) : (
                            maintenance.map((log, index) => (
                                <div key={index}>
                                    <Field
                                        type="text"
                                        data-id={index}
                                        value={log.desc}
                                        onChange={handleMaintChange}
                                        placeholder="Enter description..."
                                        autoFocus
                                    />
                                    <div className="flex items-center justify-between mt-2">
                                        <Checkbox
                                            data-id={index}
                                            checked={!!log.logAsSeen}
                                            onChange={handleCheckboxChange}
                                            label="Log as Seen"
                                            size="sm"
                                        />
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setMaintenance([]);
                                                    setNewLogCheck(false);
                                                }}
                                                className={buttonClass('quiet', 'sm')}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleSaveLogs}
                                                disabled={!log.desc.trim()}
                                                className={buttonClass('filled', 'sm')}
                                            >
                                                Save
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                        </FadeIn>
                    </div>
                )}
                </FadeIn>
            ) : (
                /* Flight Logs Tab */
                <FadeIn key="logs"><DroneLogsSection droneSerial={droneKey} /></FadeIn>
            )}
        </Panel>
    );
};

export default DroneHistory;
